/**
 * LANDING PAGES DE ANÚNCIO (/lp/*)
 *
 * Páginas de conversão usadas EXCLUSIVAMENTE como destino do Google Ads.
 * NÃO fazem parte da navegação do site, NÃO entram no sitemap e são `noindex`
 * (sem conflito de conteúdo duplicado com as páginas institucionais).
 * As páginas do site (/areas/*) permanecem intocadas.
 *
 * ORIGEM DO CONTEÚDO — nada aqui foi escrito por IA:
 *  - "usucapiao"      → texto, checklist de documentos e FAQ vindos da revisão
 *                       oficial do escritório (textos-servicos-revisao.md,
 *                       PÁGINA 04), idênticos ao que já está em data/services.ts.
 *  - "compra-e-venda" → copy enviada e aprovada pelo próprio CEO (2026-07-11):
 *                       intro, "Como Funciona a Nossa Assessoria" (4 etapas),
 *                       "Checklist Documental" e as 5 perguntas do FAQ.
 *  - "imovel-sem-matricula" → duas fontes, ambas aprovadas:
 *                       (a) os seis blocos sobre MATRÍCULA E ESCRITURA, escritos
 *                           por nós e APROVADOS pelo Dr. Luke em 08/09/2026
 *                           (social/PLANO-SEMANA-MATRICULA.md; o documento que
 *                           foi à aprovação é PARA-O-DR-LUKE-matricula.md).
 *                           Enquanto estavam marcados [RASCUNHO] não podiam ir
 *                           ao ar — e não foram;
 *                       (b) o texto de usucapião, que explica o procedimento.
 *                       Só a camada de interface é nossa: h1, title, títulos de
 *                       seção e o enunciado das perguntas. Toda RESPOSTA é
 *                       verbatim.
 *
 * Textos de interface (rótulos de botão, títulos de seção) reaproveitam as
 * palavras já usadas pelo escritório. Qualquer alteração de copy jurídica deve
 * passar pelo escritório — nunca reescrever aqui.
 */

export interface LPStep {
    title: string;
    text: string;
}

export interface LPGroup {
    label: string;
    items: string[];
    /** Sub-lista (a, b, c...) exibida indentada sob o último item do grupo. */
    sublist?: string[];
}

export interface LPFaq {
    question: string;
    answer: string;
}

export interface LandingPageData {
    slug: string;
    seo: {
        title: string;
        description: string;
        /** Card 1200x630 exibido ao compartilhar o link (WhatsApp, LinkedIn). */
        ogImage: string;
    };
    hero: {
        kicker: string;
        h1: string;
        subtitle: string;
        lead: string[];
        image: string;
        imageAlt: string;
    };
    /** Números institucionais do escritório (mesmos já publicados no site). */
    stats: { value: string; label: string }[];
    /** Blocos de texto corrido, na ordem em que aparecem. */
    prose?: {
        title: string;
        paragraphs: string[];
    }[];
    /** Etapas numeradas — usado quando o conteúdo é uma jornada/processo. */
    steps?: {
        title: string;
        subtitle?: string;
        intro: string;
        items: LPStep[];
    };
    checklist?: {
        title: string;
        intro?: string;
        groups: LPGroup[];
        note?: string;
    };
    faq: {
        title: string;
        items: LPFaq[];
    };
    closing: {
        text: string;
        buttonLabel: string;
    };
    /** Mensagem pré-preenchida do WhatsApp e id do formulário (analytics). */
    whatsappMessage: string;
    formId: string;
    /** Rótulo da área enviado junto do lead. */
    areaLabel: string;
}

export const landingPagesData: Record<string, LandingPageData> = {
    "usucapiao": {
        slug: "usucapiao",
        seo: {
            title: "Usucapião Extrajudicial e Judicial em São Paulo | Pacces Advogados",
            description:
                "Regularização de imóvel por usucapião, na via extrajudicial (cartório) ou judicial. Atuação em imóveis com contrato de gaveta ou sem escritura. Atendimento confidencial em São Paulo.",
            ogImage: "https://paccesadvogados.com.br/og-usucapiao.jpg",
        },
        hero: {
            kicker: "Direito Imobiliário · São Paulo",
            h1: "Usucapião Extrajudicial e Judicial",
            subtitle: "Regularização imobiliária pela via extrajudicial (cartório) ou judicial.",
            lead: [
                "A regularização imobiliária abrange um conjunto de procedimentos jurídicos destinados a formalizar e assegurar a titularidade de imóveis.",
            ],
            image: "https://images.unsplash.com/photo-1582407947304-fd86f028f716?auto=format&fit=crop&w=1400&q=80",
            imageAlt: "Imóvel residencial regularizado por usucapião",
        },
        stats: [
            { value: "+100", label: "Imóveis regularizados" },
            { value: "+350", label: "Clientes atendidos" },
            { value: "+2.000", label: "Processos patrocinados" },
            { value: "Nacional", label: "Atuação a nível nacional" },
        ],
        prose: [{
            title: "Como funciona o Usucapião",
            paragraphs: [
                'O Usucapião Extrajudicial se trata de um procedimento simplificado destinado à regularização imobiliária, em regra, se aplicará em hipóteses de imóveis irregulares, em que o proprietário possuí somente um instrumento particular, popularmente conhecido como "contrato de gaveta", ou, as vezes, não possuí nem este contrato, tendo, contudo, atuado como dono por longo período de tempo (05, 10 ou 15 anos) pagando impostos, contas de consumo e mantendo o imóvel.',
                "O procedimento de Usucapião Extrajudicial leva em torno de 06 (seis) meses para apartamento e 01 (um) ano para residências, podendo ser solucionado, às vezes, em prazos inferiores.",
                "Primeiramente, em muitos casos, é realizado um levantamento por um Engenheiro parceiro, que elaborará uma Planta topográfica, Memorial Descritivo e ART, após, será enviado ao Tabelião de Notas para elaboração da Ata Notarial, documento que servirá para atestar a posse sobre o imóvel.",
                "Na sequência, o escritório montará o processo e emitirá todas as certidões pertinentes para prosseguir com o protocolo perante o Registro de Imóveis competente, onde se processará o pedido.",
            ],
        }],
        checklist: {
            title: "Veja os documentos necessários para requerer o Usucapião",
            groups: [
                {
                    label: "",
                    items: [
                        "Documentos pessoais dos requerentes (RG, CPF ou CNH)",
                        "Certidão de Nascimento e / ou Casamento",
                        "Comprovante de Residência atualizado",
                        "Documentos do imóvel (Matrícula / IPTU e Contratos)",
                        "Documentos que comprovam a posse sobre o imóvel, tais como (não há necessidade de obter todos):",
                    ],
                    sublist: [
                        "Contas de consumo (água, luz, telefonia)",
                        "Pagamentos de Impostos (IPTU)",
                        "Comprovantes de melhorias realizadas no imóvel (reformas)",
                        'Contrato particular, se houver ("contrato de gaveta")',
                        "Imposto de Renda comprovando a declaração do imóvel",
                        "Correspondências recebidas no local do imóvel em nome dos requerentes",
                        "Contratos de aluguel, caso o imóvel seja alugado",
                    ],
                },
            ],
        },
        faq: {
            title: "Dúvidas frequentes do Usucapião",
            items: [
                {
                    question: "Preciso morar no imóvel para requerer a Usucapião?",
                    answer: "Não! Muitas pessoas acreditam que a Usucapião só se aplica a imóveis em que residam, mas não é verdade, a Usucapião poderá ser requerida em (i) imóveis desocupados, desde que comprovada a manutenção deste imóvel e pagamento de tributos; (ii) imóveis alugados; (iii) imóveis decorrentes de herança (até mesmo em inventário).",
                },
                {
                    question: "Preciso fazer inventário para requerer a Usucapião?",
                    answer: "Não! É possível ingressar com a Usucapião apenas com a comprovação da continuidade da posse que era exercida pelos pais passando para os filhos / herdeiros. O procedimento demandará uma comprovação de soma de posse, algo simples de se atestar.",
                },
                {
                    question: "O imóvel foi recebido por herança, ele pertencerá ao meu cônjuge também?",
                    answer: "É preciso verificar o regime de casamento, contudo, o cônjuge poderá declarar que não possuí interesse no imóvel e a Usucapião não lhe passará a propriedade. Assim como, caso queira receber o imóvel também, poderá ser feito.",
                },
                {
                    question: "Qual a vantagem do Usucapião Judicial? E sua desvantagem?",
                    answer: "Muitas vezes, em hipóteses de baixa renda, é recomendado o Usucapião Judicial para requerimento de isenção de diversas taxas, através da Justiça Gratuita, garantindo o acesso até as famílias com menor renda, à regularização do seu imóvel! A desvantagem é que este procedimento levará mais tempo do que o Extrajudicial.",
                },
                {
                    question: "Qual a vantagem do Usucapião Extrajudicial? E sua desvantagem?",
                    answer: "A vantagem é que se trata de um procedimento mais rápido que o Judicial, contudo, por se tratar de procedimento que corre diretamente em cartórios, todos os pagamentos são à vista e de valores mais elevados.",
                },
                {
                    question: "Comprei um imóvel irregular há pouco tempo, posso requerer a Usucapião?",
                    answer: "Sim! Claro! Esta é a vantagem da Usucapião! Você poderá requerer a soma de posse com o proprietário anterior, então se o antigo proprietário já detinha posse por um longo período, você poderá requerer que essa posse seja acrescida à sua, resultando no cumprimento dos requisitos para o deferimento da Usucapião.",
                },
            ],
        },
        closing: {
            text: "Faça uma reunião com um de nossos especialistas e regularize já o seu imóvel!",
            buttonLabel: "Agendar Consulta",
        },
        whatsappMessage: "Olá, gostaria de agendar uma consulta sobre Usucapião.",
        formId: "lp_usucapiao",
        areaLabel: "Usucapião",
    },

    "compra-e-venda": {
        slug: "compra-e-venda",
        seo: {
            title: "Assessoria Jurídica em Compra e Venda de Imóveis | Pacces Advogados",
            description:
                "Assessoria técnica completa na compra e venda de imóveis: Due Diligence, contrato, escritura e registro. Atendimento confidencial em São Paulo.",
            ogImage: "https://paccesadvogados.com.br/og-compra-e-venda.jpg",
        },
        hero: {
            kicker: "Direito Imobiliário · São Paulo",
            h1: "Compra e Venda de Imóveis",
            subtitle: "Segurança Jurídica Total para o seu Patrimônio",
            lead: [
                "A aquisição ou venda de um imóvel é um dos passos financeiros mais importantes da sua vida. O Pacces Advogados oferece assessoria técnica completa e especializada durante toda a jornada de negociação, blindando o seu patrimônio contra riscos ocultos, fraudes e dores de cabeça futuras.",
                "Garantimos a sua maior segurança e tranquilidade do início ao fim da transação.",
            ],
            image: "/compra-venda-imoveis.jpg",
            imageAlt: "Assinatura de contrato de compra e venda de imóvel",
        },
        stats: [
            { value: "+2.000", label: "Processos patrocinados" },
            { value: "+350", label: "Clientes atendidos" },
            { value: "+100", label: "Imóveis regularizados" },
            { value: "Nacional", label: "Atuação a nível nacional" },
        ],
        steps: {
            title: "Como Funciona a Nossa Assessoria",
            subtitle: "Sua Jornada Segura",
            intro: "Transformamos um processo complexo em uma experiência tranquila. Assim que você escolher o imóvel ideal, nossa equipe assume o controle jurídico em quatro etapas fundamentais:",
            items: [
                {
                    title: "Due Diligence Imobiliária (Raio-X Completo)",
                    text: "Realizamos uma investigação profunda de todos os documentos e certidões do imóvel, dos atuais proprietários, de suas eventuais empresas e até mesmo dos proprietários anteriores. O objetivo é identificar qualquer dívida, penhora ou processo que possa anular a sua compra.",
                },
                {
                    title: "Contratos Blindados e Personalizados",
                    text: "Com o risco aprovado, elaboramos o Compromisso de Compra e Venda com cláusulas sob medida para a sua negociação. Se a transação envolver um corretor de imóveis, nós analisamos, revisamos e ajustamos o contrato padrão dele para garantir que os seus interesses estejam 100% protegidos.",
                },
                {
                    title: "Escritura e Financiamento sem Burocracia",
                    text: "Acompanhamos o rigoroso cumprimento de tudo o que foi acordado no contrato. Conduzimos a lavratura da Escritura Pública no Tabelionato de Notas ou, se for o caso, supervisionamos a assinatura do seu contrato de financiamento bancário.",
                },
                {
                    title: "Registro e Regularização Final",
                    text: "A compra só termina quando o imóvel está no seu nome. Acompanhamos o registro da escritura no Cartório de Imóveis competente e finalizamos com a regularização cadastral perante a Prefeitura local.",
                },
            ],
        },
        checklist: {
            title: "Checklist Documental: O que analisamos na Due Diligence?",
            intro: "Para garantir que o negócio é seguro, levantamos uma extensa lista de documentos. Abaixo, separamos um exemplo das certidões e documentos básicos que passam pelo nosso pente-fino:",
            groups: [
                {
                    label: "Documentação do Imóvel",
                    items: [
                        "Matrícula atualizada com Certidão de Ônus e Ações Reipersecutórias.",
                        "Certidão Negativa de Débitos Municipais (IPTU).",
                        "Declaração de Inexistência de Débitos Condominiais (se aplicável).",
                        "Cópia do Carnê do IPTU (para conferência de metragens e dados cadastrais).",
                    ],
                },
                {
                    label: "Documentação dos Vendedores (Pessoas Físicas e Jurídicas)",
                    items: [
                        "Documentos de identificação pessoal e certidão de estado civil atualizada.",
                        "Certidões Negativas de Ações Cíveis e de Família (Justiça Estadual).",
                        "Certidões Negativas da Justiça Federal.",
                        "Certidões Negativas da Justiça do Trabalho e de Débitos Trabalhistas (CNDT).",
                        "Certidões Negativas de Protesto no domicílio dos vendedores e onde o imóvel está localizado.",
                        "Certidão Negativa de Débitos Relativos aos Tributos Federais e à Dívida Ativa da União.",
                    ],
                },
            ],
            note: "Nota: Esta lista é exemplificativa. Dependendo da complexidade do caso e de eventuais empresas no nome dos vendedores, exigiremos documentações complementares.",
        },
        faq: {
            title: "Perguntas Frequentes",
            items: [
                {
                    question: "O que é Due Diligence Imobiliária e por que ela é tão importante?",
                    answer: "A Due Diligence é uma auditoria jurídica rigorosa. Ela serve para descobrir se o imóvel tem dívidas ocultas, se os vendedores estão sofrendo processos que possam gerar a penhora do bem no futuro, ou se há problemas estruturais e ambientais. É a etapa mais importante para evitar que você perca o imóvel e o dinheiro investido.",
                },
                {
                    question: "A imobiliária já fez um contrato. Preciso mesmo de um advogado?",
                    answer: "Sim. O contrato da imobiliária ou do corretor costuma ser um modelo padrão que, muitas vezes, foca na concretização rápida da venda e no pagamento da comissão. O advogado imobiliário atua exclusivamente para defender os seus interesses, identificando cláusulas abusivas, multas desproporcionais e brechas que possam lhe causar prejuízos no futuro.",
                },
                {
                    question: "O escritório atua apenas na compra, ou também defende quem está vendendo?",
                    answer: "Atuamos em ambas as pontas. Se você é o vendedor, nossa assessoria garante que você receberá os valores de forma segura, blindando o negócio contra inadimplência e elaborando contratos que isentam você de responsabilidades indevidas após a entrega das chaves.",
                },
                {
                    question: "A Due Diligence atrasa o fechamento do negócio?",
                    answer: "Pelo contrário. Com a expertise do nosso escritório, a análise é feita de forma ágil e estruturada. Descobrir um problema antes de assinar o contrato evita anos de atrasos judiciais e bloqueios de bens. Um pequeno prazo para análise hoje garante a tranquilidade para o resto da vida.",
                },
                {
                    question: "Vocês acompanham a parte do financiamento bancário?",
                    answer: "Sim. A assessoria do escritório engloba não apenas as tratativas diretas entre comprador e vendedor, mas também a supervisão e análise dos contratos de financiamento bancário, garantindo que o repasse dos valores e a alienação fiduciária ocorram dentro da normalidade e da segurança jurídica.",
                },
            ],
        },
        closing: {
            text: "A aquisição ou venda de um imóvel é um dos passos financeiros mais importantes da sua vida. Fale com um especialista em Direito Imobiliário.",
            buttonLabel: "Agendar uma Reunião de Análise do Meu Caso",
        },
        whatsappMessage: "Olá, gostaria de falar com um especialista sobre Compra e Venda de Imóveis.",
        formId: "lp_compra_e_venda",
        areaLabel: "Compra e Venda de Imóveis",
    },
};

/**
 * /lp/imovel-sem-matricula — a MESMA página do usucapião, com outra porta.
 *
 * Quem pesquisa "imóvel sem escritura" está descrevendo o problema; "usucapião"
 * é o nome da solução. Hoje essa busca cai numa página cujo H1 diz "Usucapião" —
 * o termo pesquisado não aparece em lugar nenhum, e o Google pontua a
 * experiência da página de destino abaixo da média (IQ 3/10, CPC R$ 6,03).
 *
 * A primeira versão respondia só com o texto de usucapião, porque era o único
 * aprovado. Com a aprovação dos blocos de matrícula (08/09/2026) a página passou
 * a responder na ordem em que a dúvida existe: primeiro o que é a matrícula e
 * por que a escritura não basta, depois o procedimento.
 *
 * O que ainda NÃO está aqui, por não existir texto aprovado: os riscos de manter
 * o imóvel irregular, o que acontece quando o vendedor some ou falece, e as
 * demais vias de regularização (adjudicação compulsória, retificação de área,
 * instituição de matrícula). Pedido aberto em
 * pedido-texto-lp-imovel-sem-matricula.md.
 */
const usucapiao = landingPagesData["usucapiao"];

landingPagesData["imovel-sem-matricula"] = {
    ...usucapiao,
    slug: "imovel-sem-matricula",
    seo: {
        title: "Imóvel sem Escritura ou sem Matrícula: como regularizar | Pacces Advogados",
        description:
            "Regularização de imóvel sem escritura ou sem matrícula própria, na via extrajudicial (cartório) ou judicial. Atuação em imóveis com contrato de gaveta. Atendimento confidencial em São Paulo.",
        // Card genérico do escritório — o de usucapião traria o nome do
        // instituto, que é justamente a palavra que esta porta não usa.
        ogImage: "https://paccesadvogados.com.br/og-pacces.jpg",
    },
    hero: {
        ...usucapiao.hero,
        h1: "Imóvel sem Escritura ou sem Matrícula",
        // Subtítulo e lead são os mesmos do usucapião, palavra por palavra —
        // é a ponte entre o problema pesquisado e o procedimento.
    },
    prose: [
        {
            title: "Escritura e matrícula não são a mesma coisa",
            paragraphs: [
                "A matrícula é o registro do imóvel no Cartório de Registro de Imóveis. Cada imóvel tem uma, e apenas uma. Nela constam a descrição do bem, quem é o proprietário e o histórico do que aconteceu com ele: as transmissões anteriores, os financiamentos e as restrições. É a certidão de nascimento e o histórico do imóvel, no mesmo documento.",
                "A escritura é lavrada no Tabelionato de Notas e formaliza o negócio entre as partes. A matrícula fica no Registro de Imóveis e é onde a propriedade efetivamente se transfere.",
                "Escritura sem registro não transfere a propriedade — é a mesma razão pela qual o contrato de gaveta não transfere.",
                "O proprietário atual é aquele que consta do último registro de transmissão da matrícula. Contrato assinado, escritura lavrada e valor pago não transferem a propriedade por si. A transferência se dá com o registro na matrícula.",
                "Se o seu nome não está lá, o imóvel ainda não é seu perante terceiros — por mais que você o ocupe, pague e cuide dele há anos.",
            ],
        },
        // O procedimento vem depois da explicação do problema, e é o texto de
        // usucapião aprovado, sem alteração.
        ...(usucapiao.prose ?? []),
    ],
    faq: {
        title: "Dúvidas frequentes sobre escritura e matrícula",
        // Perguntas 1 a 6: respostas verbatim dos blocos de matrícula aprovados
        // pelo Dr. Luke em 08/09/2026. Perguntas 7 e 8 vêm do FAQ de usucapião
        // já aprovado (índices 5 e 0 de usucapiao.faq.items).
        items: [
            {
                question: "O que é a matrícula do imóvel?",
                answer: "A matrícula é o registro do imóvel no Cartório de Registro de Imóveis. Cada imóvel tem uma, e apenas uma. Nela constam a descrição do bem, quem é o proprietário e o histórico do que aconteceu com ele: as transmissões anteriores, os financiamentos e as restrições.",
            },
            {
                question: "Qual a diferença entre a escritura e a matrícula?",
                answer: "A escritura é lavrada no Tabelionato de Notas e formaliza o negócio entre as partes. A matrícula fica no Registro de Imóveis e é onde a propriedade efetivamente se transfere. Escritura sem registro não transfere a propriedade — é a mesma razão pela qual o contrato de gaveta não transfere.",
            },
            {
                question: "Comprei e paguei o imóvel. Ele já está no meu nome?",
                answer: "O proprietário atual é aquele que consta do último registro de transmissão da matrícula. Contrato assinado, escritura lavrada e valor pago não transferem a propriedade por si. A transferência se dá com o registro na matrícula. Se o seu nome não está lá, o imóvel ainda não é seu perante terceiros — por mais que você o ocupe, pague e cuide dele há anos.",
            },
            {
                question: "Como se lê uma matrícula?",
                answer: "A abertura traz o número da matrícula, o cartório e a descrição do imóvel: localização, medidas e confrontações. Abaixo dela, os atos vêm em ordem cronológica, cada um numerado. São de dois tipos. R — registro: os atos que transferem ou constituem direito sobre o imóvel, como compra e venda, doação, hipoteca, alienação fiduciária e usucapião. Av — averbação: os atos que alteram ou informam algo sobre o imóvel ou sobre o proprietário, como mudança de estado civil, construção averbada, penhora, indisponibilidade, e também os cancelamentos. A leitura correta não é achar a linha, é ver se ela foi baixada depois. Um ato pode cancelar outro. A situação atual do imóvel é a soma de tudo o que não foi cancelado — e é aí que a leitura leiga costuma errar.",
            },
            {
                question: "Onde eu consigo a certidão de matrícula?",
                answer: "A certidão de matrícula é emitida pelo Cartório de Registro de Imóveis da circunscrição em que o imóvel está localizado. Não é qualquer cartório: cada região tem o seu, e o pedido feito no cartório errado não localiza o imóvel. Pode ser solicitada presencialmente ou por meio eletrônico, pelas centrais de serviços eletrônicos dos registradores. Para localizar o imóvel, é preciso informar o número da matrícula. Não tendo o número, o cartório localiza pelo endereço completo ou pela inscrição do IPTU. A certidão retrata a situação do imóvel até a data em que foi emitida — uma certidão antiga não diz o que existe hoje.",
            },
            {
                question: "Como sei se o imóvel tem penhora ou algum bloqueio?",
                answer: "As restrições constam da própria matrícula, averbadas na sequência dos atos. Não existe documento separado onde elas se escondam. O que se procura na leitura: penhora, arresto, indisponibilidade de bens, hipoteca, alienação fiduciária, usufruto, cláusulas de inalienabilidade ou impenhorabilidade e ações que possam atingir o imóvel. Mas a matrícula limpa não encerra o assunto. Existem processos em curso que ainda não chegaram ao registro. Por isso a análise de um imóvel não para na matrícula: alcança também as certidões dos proprietários.",
            },
            usucapiao.faq.items[5],
            usucapiao.faq.items[0],
        ],
    },
    whatsappMessage: "Olá, gostaria de agendar uma consulta sobre regularização de imóvel sem escritura.",
    formId: "lp_imovel_sem_matricula",
    areaLabel: "Imóvel sem Escritura ou Matrícula",
};
